import "./DownloadAyeSearch.css";

type Platform = "macos" | "windows";
type Release = {
  available: boolean;
  url?: string;
  version?: string;
  architecture?: string;
  bytes?: number;
  sha256?: string;
};

const API = "https://api.ayesearch.com/api/ayesearch-browser/v1/manifest/";
const ART = import.meta.env.BASE_URL;

function preferredPlatform(): Platform {
  return /macintosh|mac os x/i.test(navigator.userAgent) ? "macos" : "windows";
}

function formatBytes(value?: number): string {
  return value ? `${(value / 1024 ** 3).toFixed(2)} GB` : "";
}

function renderRelease(platform: Platform, release: Release): void {
  const card = document.querySelector<HTMLElement>(`[data-platform="${platform}"]`);
  if (!card) return;
  const button = card.querySelector<HTMLAnchorElement>(".dl-platform-action");
  const status = card.querySelector<HTMLElement>(".dl-platform-status");
  const details = card.querySelector<HTMLElement>(".dl-platform-details");
  if (!button || !status || !details) return;

  if (release.available && release.url?.startsWith("https://api.ayesearch.com/")) {
    button.href = release.url;
    button.removeAttribute("aria-disabled");
    button.textContent = `Download for ${platform === "macos" ? "macOS" : "Windows"} ↗`;
    status.textContent = "Ready to download";
    status.classList.add("is-ready");
    details.textContent = [release.version, release.architecture, formatBytes(release.bytes)]
      .filter(Boolean)
      .join(" · ");
  } else {
    button.removeAttribute("href");
    button.setAttribute("aria-disabled", "true");
    button.textContent = "Coming soon";
    status.textContent = "Release being prepared";
    details.textContent = "The download will appear here after publication.";
  }
}

async function loadReleases(): Promise<void> {
  try {
    const response = await fetch(API, { cache: "no-store" });
    if (!response.ok) throw new Error(`Release manifest: ${response.status}`);
    const data = (await response.json()) as { platforms?: Record<Platform, Release> };
    for (const platform of ["macos", "windows"] as const) {
      renderRelease(platform, data.platforms?.[platform] ?? { available: false });
    }
  } catch {
    for (const platform of ["macos", "windows"] as const) {
      renderRelease(platform, { available: false });
    }
  }
}

export function renderDownloadAyeSearch(): void {
  const app = document.getElementById("app");
  if (!app) return;
  document.title = "Download AyeSearch Browser | AyeSearch";
  const platform = preferredPlatform();

  app.innerHTML = `
    <main class="dl-page">
      <div class="dl-glow dl-glow-one" aria-hidden="true"></div>
      <div class="dl-glow dl-glow-two" aria-hidden="true"></div>
      <header class="dl-header dl-wrap">
        <a class="dl-wordmark" href="/" aria-label="AyeSearch home"><span class="dl-mark">A</span><span>AyeSearch</span></a>
        <nav aria-label="Download navigation"><a href="#platforms">Downloads</a><a href="https://gaming.ayesearch.com/download/">Gaming</a><a href="/">Back to search <span aria-hidden="true">↗</span></a></nav>
      </header>

      <section class="dl-hero dl-wrap" aria-labelledby="dl-title">
        <div class="dl-hero-copy">
          <p class="dl-eyebrow"><span class="dl-live-dot"></span> AYESEARCH FOR DESKTOP</p>
          <h1 id="dl-title">Your web.<br><em>A better view.</em></h1>
          <p class="dl-intro">Take AyeSearch with you. Search, explore, and move through the web in a browser built around a focused, modern experience.</p>
          <div class="dl-hero-actions"><a class="dl-primary" href="#platforms">Choose your download <span aria-hidden="true">↓</span></a><a class="dl-text-link" href="https://gaming.ayesearch.com/download/">Looking for AyeSearch Gaming? ↗</a></div>
          <div class="dl-hero-meta"><span>Desktop browser</span><span>macOS & Windows</span><span>Free to download</span></div>
        </div>
        <div class="dl-showcase" aria-label="AyeSearch Browser preview">
          <div class="dl-showcase-orbit" aria-hidden="true"></div>
          <div class="dl-window"><div class="dl-window-top"><span class="dl-dots">● ● ●</span><span class="dl-address">ayesearch.com</span><span>✦</span></div><img src="${ART}us/aysearch-browser.png" alt="AyeSearch Browser interface" loading="eager"></div>
          <div class="dl-floating-label"><span>✦</span> Discover more</div>
        </div>
      </section>

      <section class="dl-download-section" id="platforms" aria-labelledby="dl-platforms-title">
        <div class="dl-wrap">
          <div class="dl-section-heading"><p class="dl-eyebrow">GET STARTED</p><h2 id="dl-platforms-title">Find your version.</h2><p>Choose your desktop platform. Release details appear as soon as a verified build is published.</p></div>
          <div class="dl-platform-grid">
            <article class="dl-platform-card ${platform === "macos" ? "is-recommended" : ""}" data-platform="macos"><div class="dl-card-top"><span class="dl-platform-icon" aria-hidden="true">⌘</span><span class="dl-platform-status">Checking availability…</span></div><h3>For macOS</h3><p>AyeSearch Browser for your Mac.</p><div class="dl-platform-details" aria-live="polite">Checking the latest release</div><a class="dl-platform-action" aria-disabled="true">Checking…</a></article>
            <article class="dl-platform-card ${platform === "windows" ? "is-recommended" : ""}" data-platform="windows"><div class="dl-card-top"><span class="dl-platform-icon" aria-hidden="true">⊞</span><span class="dl-platform-status">Checking availability…</span></div><h3>For Windows</h3><p>AyeSearch Browser for your PC.</p><div class="dl-platform-details" aria-live="polite">Checking the latest release</div><a class="dl-platform-action" aria-disabled="true">Checking…</a></article>
          </div>
          <p class="dl-download-note">Available downloads are served from AyeSearch. Verify the version and file checksum in the release details before installing.</p>
        </div>
      </section>

      <section class="dl-feature-section dl-wrap" aria-labelledby="dl-feature-title"><div class="dl-feature-copy"><p class="dl-eyebrow">BUILT FOR THE WAY YOU EXPLORE</p><h2 id="dl-feature-title">The web, with room to think.</h2><p>Keep search close, discover new ideas, and browse in a clean space that puts the page first.</p><a class="dl-text-link" href="/">Explore AyeSearch ↗</a></div><div class="dl-feature-image"><img src="${ART}us/aysearch-incognito.png" alt="AyeSearch private browsing preview" loading="lazy"></div></section>

      <section class="dl-more-section"><div class="dl-wrap dl-more-inner"><div><p class="dl-eyebrow">MORE TO EXPLORE</p><h2>Looking for the games?</h2><p>AyeSearch Gaming has its own desktop download page.</p></div><a href="https://gaming.ayesearch.com/download/">Visit Gaming downloads ↗</a></div></section>
      <footer class="dl-footer dl-wrap"><span>© 2026 AyeSearch</span><div><a href="/">Search</a><a href="https://gaming.ayesearch.com/download/">Gaming downloads</a><a href="mailto:feedback@amailme.com?subject=AyeSearch%20Feedback">Feedback</a></div></footer>
    </main>
  `;
  void loadReleases();
}

export default renderDownloadAyeSearch;
